import { Loader2, Sparkles, Square } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../../api/rest";
import type { BulkStatus, ShopText } from "../../types/shop-texts";
import {
	btn,
	ConfirmDialog,
	ErrorBanner,
	fmtDateTime,
	input,
} from "../flows/ui";

const POLL_MS = 5000;
const SINGLE_POLL_MS = 3000;
const SINGLE_POLL_LIMIT_MS = 120_000;

type Scope = "all" | "missing";

export const ShopTextBulkPanel = () => {
	const [status, setStatus] = useState<BulkStatus | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [confirm, setConfirm] = useState<Scope | null>(null);
	const [busy, setBusy] = useState(false);
	const [slug, setSlug] = useState("");
	const [single, setSingle] = useState<{
		state: "working" | "done" | "failed";
		text: string;
	} | null>(null);

	const load = useCallback(async () => {
		try {
			setStatus(await fetchJson<BulkStatus>("/shop-texts/status"));
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	useEffect(() => {
		if (!status?.running) return;
		const timer = setInterval(load, POLL_MS);
		return () => clearInterval(timer);
	}, [status?.running, load]);

	const start = async (scope: Scope) => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{ started: boolean; reason?: string }>(
				"/shop-texts/bulk",
				"POST",
				{ scope },
			);
			if (!result.started) setError(result.reason ?? "Could not start");
			setConfirm(null);
			await load();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	};

	const cancel = async () => {
		await sendJson("/shop-texts/bulk/cancel", "POST");
		await load();
	};

	const rewriteOne = async () => {
		setError(null);
		const value = slug.trim();
		if (!value) return;
		try {
			const queued = await sendJson<{
				queued: boolean;
				merchantId?: number;
				error?: string;
			}>("/shop-texts/rewrite", "POST", { slug: value });
			if (!queued.queued || !queued.merchantId) {
				setSingle({ state: "failed", text: queued.error ?? "Not found" });
				return;
			}
			const id = queued.merchantId;
			const before = await fetchJson<ShopText>(`/shop-texts/merchants/${id}`);
			setSingle({ state: "working", text: `Rewriting ${before.name}…` });
			const started = Date.now();
			const timer = setInterval(async () => {
				const next = await fetchJson<ShopText>(
					`/shop-texts/merchants/${id}`,
				).catch(() => null);
				if (next && (next.at !== before.at || next.error !== before.error)) {
					clearInterval(timer);
					setSingle(
						next.at !== before.at && next.text
							? { state: "done", text: `${next.name}: ${next.text}` }
							: {
									state: "failed",
									text: `${next.name}: ${next.error ?? "failed"}`,
								},
					);
					load();
				} else if (Date.now() - started > SINGLE_POLL_LIMIT_MS) {
					clearInterval(timer);
					setSingle({
						state: "failed",
						text: "Still working. Check the shop page in a minute.",
					});
				}
			}, SINGLE_POLL_MS);
		} catch (e) {
			setSingle({
				state: "failed",
				text: e instanceof Error ? e.message : String(e),
			});
		}
	};

	const run = status?.run;
	const processed = run ? run.written + run.failed + run.skipped : 0;
	const pct =
		run && run.total > 0 ? Math.round((processed / run.total) * 100) : 0;
	const missing = status
		? status.shops.total - status.shops.withText + status.shops.stale
		: 0;

	return (
		<div className="card mb-6 bg-white p-5">
			<div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
				<div>
					<h3 className="text-sm font-semibold text-slate-700">Shop texts</h3>
					<p className="text-[12px] text-slate-500">
						Rewrite the shop descriptions shoppers see, in PriceOnline&apos;s
						tone of voice, from each network&apos;s own text.
						{status &&
							` ${status.shops.withText} of ${status.shops.total} validated shops have one; ${status.shops.stale} are out of date and ${status.shops.withError} failed last time.`}
					</p>
				</div>
				{status?.running ? (
					<button type="button" className={btn.danger} onClick={cancel}>
						<Square className="h-4 w-4" /> Stop
					</button>
				) : (
					<div className="flex gap-2">
						<button
							type="button"
							className={btn.ghost}
							disabled={busy}
							onClick={() => setConfirm("missing")}
						>
							Rewrite missing & outdated
						</button>
						<button
							type="button"
							className={btn.primary}
							disabled={busy}
							onClick={() => setConfirm("all")}
						>
							<Sparkles className="h-4 w-4" /> Rewrite all
						</button>
					</div>
				)}
			</div>

			<ErrorBanner error={error} />

			{run && (
				<div className="mb-4 text-[12px] text-slate-600">
					<div className="mb-1 flex justify-between">
						<span>
							{status?.running ? "Running" : `Last run ${run.status}`} ·{" "}
							{run.scope === "all" ? "all shops" : "missing & outdated"} ·{" "}
							{processed} of {run.total} · {run.written} written, {run.failed}{" "}
							failed
						</span>
						<span>{fmtDateTime(run.startedAt)}</span>
					</div>
					<div className="h-2 overflow-hidden rounded bg-slate-100">
						<div
							className="h-full bg-primary transition-all"
							style={{ width: `${pct}%` }}
						/>
					</div>
					{run.error && <p className="mt-1 text-red-700">{run.error}</p>}
				</div>
			)}

			<div className="flex flex-wrap items-center gap-2">
				<input
					className={`${input.replace("w-full ", "")} w-72`}
					placeholder="Shop slug or id, e.g. navnelapper"
					value={slug}
					onChange={(e) => setSlug(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && rewriteOne()}
				/>
				<button
					type="button"
					className={btn.ghost}
					disabled={!slug.trim() || single?.state === "working"}
					onClick={rewriteOne}
				>
					{single?.state === "working" ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Sparkles className="h-4 w-4" />
					)}
					Rewrite this shop
				</button>
			</div>
			{single && (
				<p
					className={`mt-2 text-[12px] ${single.state === "failed" ? "text-red-700" : "text-slate-600"}`}
				>
					{single.text}
				</p>
			)}

			{confirm && (
				<ConfirmDialog
					title={
						confirm === "all"
							? "Rewrite every shop text?"
							: "Rewrite missing and outdated shop texts?"
					}
					body={
						confirm === "all" ? (
							<>
								This rewrites the text for all {status?.shops.total ?? ""}{" "}
								validated shops, one at a time, and replaces every existing
								text, including ones edited by hand. It uses the Ollama quota
								and takes roughly one to two hours. You can stop it at any time.
							</>
						) : (
							<>
								This writes a text for the {missing} shops that have none or
								whose network text has changed since. Texts edited by hand are
								only touched if their network text changed.
							</>
						)
					}
					confirmLabel={confirm === "all" ? "Rewrite all" : "Rewrite missing"}
					danger={confirm === "all"}
					busy={busy}
					onCancel={() => setConfirm(null)}
					onConfirm={() => start(confirm)}
				/>
			)}
		</div>
	);
};
